import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Lock, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { isLockedStatus, ORDER_STATUS_LABELS, type MaintenanceVisibility } from '@oficina/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchSelect, type SearchSelectOption } from '@/components/common/search-select';
import { StatusBadge } from '@/components/common/status-badge';
import { ErrorState } from '@/components/common/states';
import { TimelineItem } from '@/components/orders/maintenance-card';
import { MaintenanceFilePicker } from '@/components/orders/maintenance-file-picker';
import { MaintenanceAttachments } from '@/components/orders/maintenance-attachments';
import { MaintenanceEditDialog } from '@/components/orders/maintenance-edit-dialog';
import { MaintenanceDeleteButton } from '@/components/orders/maintenance-delete-button';
import { MaintenanceVisibilityBadge, MaintenanceVisibilitySelect } from '@/components/orders/maintenance-visibility';
import { useAuth } from '@/hooks/use-auth';
import { useOrder, useOrdersList } from '@/hooks/queries';
import { useDebounce } from '@/hooks/use-debounce';
import { ordersService } from '@/services/orders.service';
import { errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { qk } from '@/lib/query-keys';

interface QuickServiceContextValue {
  /** Abre a janela. Com `orderId`, a OS já vem selecionada. */
  open: (orderId?: string) => void;
}

const QuickServiceContext = createContext<QuickServiceContextValue | null>(null);

export function useQuickService(): QuickServiceContextValue {
  const ctx = useContext(QuickServiceContext);
  if (!ctx) throw new Error('useQuickService precisa estar dentro de <QuickServiceProvider>.');
  return ctx;
}

/**
 * Janela "Registrar serviço": escolha a OS (busca por número, cliente ou aparelho), veja o histórico
 * dela e registre um novo serviço sem abrir a ordem. Permanece aberta para registrar em outra OS.
 */
export function QuickServiceProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const open = useCallback((id?: string) => {
    setOrderId(id ?? null);
    setIsOpen(true);
  }, []);
  const value = useMemo(() => ({ open }), [open]);

  return (
    <QuickServiceContext.Provider value={value}>
      {children}
      <QuickServiceDialog open={isOpen} onOpenChange={setIsOpen} orderId={orderId} onOrderChange={setOrderId} />
    </QuickServiceContext.Provider>
  );
}

interface QuickServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
  onOrderChange: (id: string | null) => void;
}

function QuickServiceDialog({ open, onOpenChange, orderId, onOrderChange }: QuickServiceDialogProps) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // Padrão seguro: interno. A equipe marca explicitamente o que o cliente pode ver.
  const [visibility, setVisibility] = useState<MaintenanceVisibility>('INTERNAL');
  useEffect(() => {
    setFiles([]);
    setText('');
    setVisibility('INTERNAL');
  }, [orderId]);
  const debounced = useDebounce(search, 250);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setText('');
      setFiles([]);
      setVisibility('INTERNAL');
    }
  }, [open]);

  const list = useOrdersList({ search: debounced || undefined, pageSize: 20, sort: 'recent' });
  const order = useOrder(open ? (orderId ?? undefined) : undefined);
  const detail = order.data;
  const locked = detail ? isLockedStatus(detail.status) : false;

  const options = useMemo<SearchSelectOption[]>(
    () =>
      (list.data?.data ?? []).map((o) => ({
        value: o.id,
        label: `OS ${o.number} — ${o.client.name}`,
        description: `${o.equipment.brand} ${o.equipment.model} · ${ORDER_STATUS_LABELS[o.status]}${
          isLockedStatus(o.status) ? ' (encerrada)' : ''
        }`,
        disabled: isLockedStatus(o.status),
      })),
    [list.data],
  );

  const mutation = useMutation({
    mutationFn: (description: string) => ordersService.addMaintenance(orderId!, description, files, visibility),
    onSuccess: () => {
      setText('');
      setFiles([]);
      setVisibility('INTERNAL');
      void qc.invalidateQueries({ queryKey: qk.orders.detail(orderId!) });
      void qc.invalidateQueries({ queryKey: qk.orders.all });
      void qc.invalidateQueries({ queryKey: qk.clients.all });
      void qc.invalidateQueries({ queryKey: qk.equipment.all });
      toast.success(`Serviço registrado na OS ${detail?.number ?? ''}.`.trim());
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = () => {
    if (mutation.isPending) return;
    const description = text.trim();
    if (!orderId) return toast.error('Selecione a ordem de serviço.');
    if (description.length < 2) return toast.error('Descreva o serviço antes de registrar.');
    mutation.mutate(description);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Registrar serviço</DialogTitle>
          <DialogDescription>
            Escolha a ordem e adicione ao histórico. Data, hora e usuário são preenchidos automaticamente.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quick-order">Ordem de serviço</Label>
            <SearchSelect
              id="quick-order"
              value={orderId}
              onChange={(value) => onOrderChange(value)}
              options={options}
              selectedLabel={detail ? `OS ${detail.number} — ${detail.client.name}` : undefined}
              placeholder="Buscar por número, cliente ou aparelho…"
              searchPlaceholder="Digite o número, cliente ou aparelho…"
              emptyText="Nenhuma ordem encontrada."
              loading={list.isFetching}
              onSearchChange={setSearch}
            />
          </div>

          {orderId && order.isError && <ErrorState error={order.error} onRetry={() => void order.refetch()} />}

          {detail && (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-[#f8fafc] px-3 py-2.5 text-xs">
                <StatusBadge status={detail.status} />
                <span>
                  <b>{detail.client.name}</b> · {detail.equipment.brand} {detail.equipment.model}
                </span>
                <Link
                  to={`/ordens/${detail.id}`}
                  onClick={() => onOpenChange(false)}
                  className="ml-auto font-semibold text-primary hover:underline"
                >
                  Abrir OS
                </Link>
              </div>

              {locked ? (
                <p className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
                  <Lock className="h-4 w-4 shrink-0" aria-hidden />
                  Esta ordem está {ORDER_STATUS_LABELS[detail.status].toLowerCase()} e não aceita novos registros. Um
                  administrador pode reabri-la.
                </p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="quick-text">Serviço realizado</Label>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <Textarea
                      className="min-h-24 flex-1"
                      disabled={mutation.isPending}
                      id="quick-text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      maxLength={2000}
                      rows={3}
                      autoFocus
                      placeholder="Troca de tela, limpeza interna, substituição da bateria…"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
                      }}
                    />
                    <MaintenanceFilePicker files={files} onChange={setFiles} disabled={mutation.isPending} />
                  </div>
                  <div className="mt-1">
                    <MaintenanceVisibilitySelect
                      idPrefix="quick-service"
                      value={visibility}
                      onChange={setVisibility}
                      disabled={mutation.isPending}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Ctrl + Enter para registrar.</p>
                </div>
              )}

              <div>
                <h3 className="mb-2 text-[13px] font-bold">Histórico desta ordem</h3>
                <ol className="max-h-56 overflow-y-auto pr-1">
                  {detail.maintenanceHistory.length === 0 && (
                    <li className="text-xs text-muted-foreground">Nenhum serviço registrado até o momento.</li>
                  )}
                  {detail.maintenanceHistory.map((e) => (
                    <TimelineItem
                      key={e.id}
                      title={`Técnico: ${e.authorName}`}
                      badge={<MaintenanceVisibilityBadge visibility={e.visibility} />}
                      meta={`Data e hora: ${formatDateTime(e.createdAt)}`}
                      text={e.description}
                      action={
                        orderId ? (
                          <div className="flex items-center gap-1">
                            {!locked && user && can('orders.write') && e.userId === user.id && (
                              <MaintenanceEditDialog orderId={orderId} entry={e} />
                            )}
                            {user?.role === 'ADMIN' && <MaintenanceDeleteButton orderId={orderId} entry={e} />}
                          </div>
                        ) : undefined
                      }
                      children={orderId ? <MaintenanceAttachments orderId={orderId} entry={e} /> : undefined}
                    />
                  ))}
                </ol>
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button type="button" onClick={submit} loading={mutation.isPending} disabled={!orderId || locked}>
            <Plus /> Registrar serviço
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
